import React, { useEffect } from "react";
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";

import { useNavigate } from "../../../components/Navigate";
import MainLayout from "../../../components/layout/MainLayout";
import ScreenWrapper from "../../../components/layout/screenWrapper";
import {
  tdoeReportsRegistry,
  TDOE_CHART_TYPES,
  TDOE_CATEGORIES,
} from "../components/reports/tdoeReportsRegistry";

import "../reportsMgmt.scss";

// Fixed top-level display order and labels for the category accordions.
// Data Collection Period (BOY/MOY/EOY) is a report parameter on every TDOE
// report rather than a grouping level here, so it is not sectioned
// separately - entries that are locked to a specific period (or a
// multi-period comparison) carry that as report parameters instead.
const TDOE_CATEGORY_ORDER = [
  { category: TDOE_CATEGORIES.CULTURE_OF_LEARNING, label: "Culture of Learning" },
  { category: TDOE_CATEGORIES.CORE_ACTION_1, label: "Core Action 1" },
  { category: TDOE_CATEGORIES.CORE_ACTION_2, label: "Core Action 2" },
  { category: TDOE_CATEGORIES.CORE_ACTION_3, label: "Core Action 3" },
];

const groupByCategory = (registry) => {
  const buckets = {};
  registry.forEach((report) => {
    if (!buckets[report.category]) {
      buckets[report.category] = [];
    }
    buckets[report.category].push(report);
  });
  return buckets;
};

// Categories whose entries include lettered sub-indicators (Core Action
// 2.A-2.E, 3.A-3.C) that should be grouped into their own nested
// sub-accordion. Culture of Learning and Core Action 1 have none, so they
// render as a flat list only.
const CATEGORIES_WITH_SUB_INDICATORS = new Set([
  TDOE_CATEGORIES.CORE_ACTION_2,
  TDOE_CATEGORIES.CORE_ACTION_3,
]);

// Splits a category's entries into entries with no sub-indicator (rendered
// directly in the category's list) and sub-indicator buckets (rendered as
// nested sub-accordions), sorted lexicographically (matches letter order).
const groupBySubIndicator = (entries) => {
  const flatEntries = [];
  const subIndicatorBuckets = {};
  entries.forEach((report) => {
    if (!report.subIndicator) {
      flatEntries.push(report);
      return;
    }
    if (!subIndicatorBuckets[report.subIndicator]) {
      subIndicatorBuckets[report.subIndicator] = [];
    }
    subIndicatorBuckets[report.subIndicator].push(report);
  });
  const subIndicatorOrder = Object.keys(subIndicatorBuckets).sort();
  return { flatEntries, subIndicatorBuckets, subIndicatorOrder };
};

const getReportUrl = (report) => {
  let url;
  switch (report.chartType) {
    case TDOE_CHART_TYPES.PIE:
      url = "/report/tdoe-pie-chart";
      break;
    case TDOE_CHART_TYPES.BAR:
      url = "/report/tdoe-bar-chart";
      break;
    case TDOE_CHART_TYPES.STACKED_BAR:
      url = "/report/tdoe-stacked-bar-chart";
      break;
    case TDOE_CHART_TYPES.CLUSTER_BAR:
      url = "/report/tdoe-cluster-bar-chart";
      break;
    case TDOE_CHART_TYPES.PERIOD_BREAKDOWN:
      url = "/report/tdoe-period-breakdown-chart";
      break;
    case TDOE_CHART_TYPES.HORIZONTAL_BAR:
      url = "/report/tdoe-horizontal-bar-chart";
      break;
    case TDOE_CHART_TYPES.TEACHERS_BY_PERIOD:
      url = "/report/tdoe-teachers-by-period-chart";
      break;
    default:
      url = "/report/tdoe-bar-chart";
  }

  const params = new URLSearchParams();
  params.set("title", report.title);
  // Entries that were de-duplicated across BOY/MOY (see tdoeReportsRegistry.js)
  // carry no dataCollectionPeriod, letting the report viewer apply its own
  // default (still adjustable there via the Data Collection Period parameter).
  if (report.dataCollectionPeriod) {
    params.set("dcp", report.dataCollectionPeriod);
  }

  if (report.chartType === TDOE_CHART_TYPES.CLUSTER_BAR) {
    // Grade Level is no longer locked per entry (see tdoeReportsRegistry.js);
    // it's now a required report parameter the user selects after opening
    // the report, same pattern as the de-duplicated Data Collection Period
    // and Group By parameters above.
    if (report.gradeLevel) {
      params.set("gradeLevel", report.gradeLevel);
    }
    params.set("coreAction", report.coreAction);
  } else {
    params.set("qstf", report.questionSurveyTemplateFieldId);
    if (report.defaultGroupBy) {
      params.set("groupBy", report.defaultGroupBy);
    }
  }

  return `${url}?${params.toString()}`;
};

const DataDashboardReportsMgmt = () => {
  const navigate = useNavigate();

  useEffect(() => {
    const screen = window.document.getElementsByClassName("screen");
    if (screen.length > 0) {
      screen[0].scrollTo({ top: 0, behavior: "smooth" });
    }
  }, []);

  const handleReportClick = (report) => {
    navigate(getReportUrl(report), { state: "VISITED" });
  };

  const renderReportEntries = (reports) =>
    reports.map((report) => (
      <div className="categoryWrapper" key={report.id}>
        {report.hasDefinition ? (
          <h3
            className="categoryTitle report report-title"
            onClick={() => handleReportClick(report)}
          >
            {report.title}
          </h3>
        ) : (
          <h3 className="categoryTitle report report-title no-link">
            {report.title}
            <span className="coming-soon-badge">Coming soon</span>
          </h3>
        )}
        <h4 className="report-description">
          {report.hasDefinition
            ? "Click to view the TDOE report."
            : "This report is not yet available."}
        </h4>
      </div>
    ));

  const renderCategoryContents = (category, entries) => {
    if (!CATEGORIES_WITH_SUB_INDICATORS.has(category)) {
      return renderReportEntries(entries);
    }

    const { flatEntries, subIndicatorBuckets, subIndicatorOrder } =
      groupBySubIndicator(entries);

    return (
      <>
        {renderReportEntries(flatEntries)}
        {subIndicatorOrder.map((subIndicator) => (
          <Accordion key={subIndicator}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <h4 className="sub-indicator-title">
                Core Action {subIndicator}
              </h4>
            </AccordionSummary>
            <AccordionDetails>
              {renderReportEntries(subIndicatorBuckets[subIndicator])}
            </AccordionDetails>
          </Accordion>
        ))}
      </>
    );
  };

  const renderCategorySections = (registry) => {
    const buckets = groupByCategory(registry);
    return TDOE_CATEGORY_ORDER.filter(
      ({ category }) => buckets[category] && buckets[category].length > 0
    ).map(({ category, label }) => (
      <Accordion key={category}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <h2>{label}</h2>
        </AccordionSummary>
        <AccordionDetails>
          {renderCategoryContents(category, buckets[category])}
        </AccordionDetails>
      </Accordion>
    ));
  };

  return (
    <>
      <MainLayout>
        <ScreenWrapper loading={false} loadingError={false} onReturn={() => {}}>
          <div className="reports">
            <div className="categoryWrapper reports-title">
              <h1>Data Dashboard Reports</h1>
            </div>
            <div className="contentDisplay">
              {renderCategorySections(tdoeReportsRegistry)}
            </div>
          </div>
        </ScreenWrapper>
      </MainLayout>
    </>
  );
};

export default DataDashboardReportsMgmt;
